/**
 * Status badge for change proposals.
 *
 * Per UI_GUIDELINES_v1 the chrome is monochrome — but proposal status is a
 * "true indicator" surface and uses the status palette explicitly. The
 * mapping mirrors the canonical taxonomy in @chaos/types/constitution:
 *
 *   pending_founder_approval / in_review → warning (yellow)
 *   approved                              → success (green)
 *   applied                               → success (green, filled)
 *   rejected / failed                     → destructive (red)
 *   queued_offline                        → muted (gray)
 *   auto_activated                        → success (green, subtle)
 */
import type { ProposalWorkflowStatus } from '@chaos/types/constitution';

import { cn } from '../../lib/utils';

interface Props {
  status: ProposalWorkflowStatus;
  className?: string;
}

const LABELS: Record<ProposalWorkflowStatus, string> = {
  pending_founder_approval: 'Pending',
  in_review: 'In review',
  approved: 'Approved',
  applied: 'Applied',
  rejected: 'Rejected',
  failed: 'Failed',
  queued_offline: 'Queued',
  auto_activated: 'Auto',
};

export const PROPOSAL_STATUS_CLASS: Record<ProposalWorkflowStatus, string> = {
  pending_founder_approval: 'border-warning/40 bg-warning/10 text-warning-foreground',
  in_review: 'border-warning/40 bg-warning/10 text-warning-foreground',
  approved: 'border-success/40 bg-success/10 text-success-foreground',
  applied: 'border-success/60 bg-success/15 text-success-foreground',
  rejected: 'border-destructive/40 bg-destructive/10 text-destructive-foreground',
  failed: 'border-destructive/50 bg-destructive/10 text-destructive-foreground',
  queued_offline: 'border-border bg-muted text-muted-foreground',
  auto_activated: 'border-success/30 bg-success/5 text-success-foreground',
};

export function ProposalStatusBadge({ status, className }: Props) {
  return (
    <span
      role="status"
      className={cn(
        'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
        PROPOSAL_STATUS_CLASS[status],
        className,
      )}
      aria-label={`Status: ${LABELS[status]}`}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          status === 'pending_founder_approval' || status === 'in_review' ? 'bg-warning' : '',
          status === 'approved' || status === 'applied' || status === 'auto_activated'
            ? 'bg-success'
            : '',
          status === 'rejected' || status === 'failed' ? 'bg-destructive' : '',
          status === 'queued_offline' ? 'bg-muted-foreground/60' : '',
        )}
      />
      {LABELS[status]}
    </span>
  );
}
